import * as XLSX from "xlsx";

export type BulkFieldType = "string" | "number" | "date";

export interface BulkUploadField {
  key: string;
  label: string;
  required: boolean;
  type?: BulkFieldType;
  defaultColumnName: string;
  hint?: string;
  example?: string;
}

export interface SpreadsheetData {
  fileName: string;
  headers: string[];
  rows: Record<string, string>[];
}

export interface MappedRow {
  rowNumber: number;
  data: Record<string, unknown>;
  errors: string[];
}

export type ColumnMapping = Record<string, string>;

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_ROWS = 1000;

export function normalizeHeader(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

export function getActiveFields(
  fields: BulkUploadField[],
  includedKeys: Record<string, boolean>,
): BulkUploadField[] {
  return fields.filter((field) => field.required || includedKeys[field.key]);
}

export function buildDefaultMapping(fields: BulkUploadField[]): ColumnMapping {
  return fields.reduce<ColumnMapping>((acc, field) => {
    acc[field.key] = field.defaultColumnName;
    return acc;
  }, {});
}

export function buildDefaultIncluded(
  fields: BulkUploadField[],
): Record<string, boolean> {
  return fields.reduce<Record<string, boolean>>((acc, field) => {
    acc[field.key] = true;
    return acc;
  }, {});
}

export function validateMapping(
  fields: BulkUploadField[],
  mapping: ColumnMapping,
  includedKeys: Record<string, boolean>,
): string[] {
  const errors: string[] = [];
  const active = getActiveFields(fields, includedKeys);
  const seen = new Map<string, string>();

  active.forEach((field) => {
    const columnName = mapping[field.key]?.trim() ?? "";
    if (!columnName) {
      errors.push(`Enter a column name for ${field.label}.`);
      return;
    }

    const normalized = normalizeHeader(columnName);
    const existing = seen.get(normalized);
    if (existing) {
      errors.push(
        `"${columnName}" is mapped to both ${existing} and ${field.label}.`,
      );
    } else {
      seen.set(normalized, field.label);
    }
  });

  return errors;
}

export function matchSpreadsheetHeaders(
  headers: string[],
  fields: BulkUploadField[],
  mapping: ColumnMapping,
  includedKeys: Record<string, boolean>,
): {
  matched: BulkUploadField[];
  missing: { field: BulkUploadField; columnName: string }[];
  unused: string[];
} {
  const headerSet = new Map(
    headers.filter(Boolean).map((header) => [normalizeHeader(header), header]),
  );
  const active = getActiveFields(fields, includedKeys);
  const matched: BulkUploadField[] = [];
  const missing: { field: BulkUploadField; columnName: string }[] = [];
  const usedNormalized = new Set<string>();

  active.forEach((field) => {
    const columnName = mapping[field.key]?.trim() ?? "";
    const found = headerSet.get(normalizeHeader(columnName));
    if (found) {
      matched.push(field);
      usedNormalized.add(normalizeHeader(columnName));
    } else {
      missing.push({ field, columnName });
    }
  });

  const unused = headers.filter(
    (header) => header && !usedNormalized.has(normalizeHeader(header)),
  );

  return { matched, missing, unused };
}

function coerceNumber(value: string, label: string): number | string {
  const cleaned = value.replace(/,/g, "").trim();
  if (!cleaned) return "";
  const parsed = Number(cleaned);
  if (Number.isNaN(parsed)) {
    throw new Error(`${label} must be a number`);
  }
  return parsed;
}

function excelSerialToIso(serial: number): string {
  const utc = Date.UTC(1899, 11, 30) + serial * 86400000;
  return new Date(utc).toISOString().slice(0, 10);
}

function coerceDate(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }

  const serial = Number(trimmed);
  if (trimmed !== "" && Number.isFinite(serial) && serial > 20000 && serial < 80000) {
    return excelSerialToIso(serial);
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-CA");
  }

  throw new Error(`${label} must be a valid date`);
}

function coerceValue(
  raw: string,
  field: BulkUploadField,
): unknown {
  const type = field.type ?? "string";
  if (type === "number") {
    return coerceNumber(raw, field.label);
  }
  if (type === "date") {
    return coerceDate(raw, field.label);
  }
  return raw.trim();
}

export function mapSpreadsheetRows(
  spreadsheet: SpreadsheetData,
  fields: BulkUploadField[],
  mapping: ColumnMapping,
  includedKeys: Record<string, boolean>,
  validateRecord?: (record: Record<string, unknown>) => string[],
  transformRecord?: (
    record: Record<string, unknown>,
  ) => Record<string, unknown>,
): MappedRow[] {
  const active = getActiveFields(fields, includedKeys);
  const headerLookup = new Map(
    spreadsheet.headers.map((header) => [normalizeHeader(header), header]),
  );

  return spreadsheet.rows
    .map((row, index) => {
      const data: Record<string, unknown> = {};
      const errors: string[] = [];
      let hasAnyValue = false;

      active.forEach((field) => {
        const columnName = mapping[field.key]?.trim() ?? "";
        const header = headerLookup.get(normalizeHeader(columnName));
        const raw = header ? String(row[header] ?? "").trim() : "";

        if (raw) hasAnyValue = true;

        if (!raw) {
          if (field.required) {
            errors.push(`${field.label} is required`);
          }
          return;
        }

        try {
          data[field.key] = coerceValue(raw, field);
        } catch (error) {
          errors.push(
            error instanceof Error ? error.message : `Invalid ${field.label}`,
          );
        }
      });

      if (!hasAnyValue) {
        return null;
      }

      const record = transformRecord ? transformRecord(data) : data;

      if (validateRecord && errors.length === 0) {
        errors.push(...validateRecord(record));
      }

      return {
        rowNumber: index + 2,
        data: record,
        errors,
      };
    })
    .filter((row): row is MappedRow => row !== null);
}

function cellToString(value: unknown): string {
  if (value == null) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString("en-CA");
  }
  return String(value).trim();
}

function uniquifyHeaders(
  headerRow: string[],
): { headers: string[]; indexes: number[] } {
  const headers: string[] = [];
  const indexes: number[] = [];
  const seen = new Map<string, number>();

  headerRow.forEach((header, index) => {
    if (!header) return;
    const normalized = normalizeHeader(header);
    const count = seen.get(normalized) ?? 0;
    seen.set(normalized, count + 1);
    headers.push(count === 0 ? header : `${header} (${count + 1})`);
    indexes.push(index);
  });

  return { headers, indexes };
}

export async function parseSpreadsheet(file: File): Promise<SpreadsheetData> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("File is too large. Maximum size is 10MB.");
  }

  const extension = file.name.split(".").pop()?.toLowerCase();
  if (!["xlsx", "xls", "csv"].includes(extension || "")) {
    throw new Error("Upload an Excel (.xlsx, .xls) or CSV file.");
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: "array",
    cellDates: true,
    raw: false,
  });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error("The spreadsheet is empty.");
  }

  const sheet = workbook.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json<(string | number | Date | null)[]>(
    sheet,
    {
      header: 1,
      defval: "",
      raw: false,
      blankrows: false,
    },
  );

  const headerRow = (matrix[0] || []).map((cell) => cellToString(cell));
  const { headers, indexes } = uniquifyHeaders(headerRow);

  if (headers.length === 0) {
    throw new Error("No column headers were found in the first row.");
  }

  const dataRows = matrix.slice(1);
  if (dataRows.length > MAX_ROWS) {
    throw new Error(`Too many rows. Upload ${MAX_ROWS} rows or fewer.`);
  }

  const rows = dataRows
    .map((row) => {
      const record: Record<string, string> = {};
      let hasValue = false;
      headers.forEach((header, index) => {
        const value = cellToString(row?.[indexes[index]]);
        record[header] = value;
        if (value) hasValue = true;
      });
      return hasValue ? record : null;
    })
    .filter((row): row is Record<string, string> => row !== null);

  if (rows.length === 0) {
    throw new Error("The spreadsheet has headers but no data rows.");
  }

  return {
    fileName: file.name,
    headers,
    rows,
  };
}

export function downloadExcelTemplate(
  headers: string[],
  fileName: string,
  sheetName = "Sheet1",
): void {
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  worksheet["!cols"] = headers.map((header) => ({
    wch: Math.max(header.length + 4, 18),
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

const STORAGE_PREFIX = "fleetpro:bulk-upload:";

export function loadSavedBulkConfig(storageKey: string): {
  mapping: ColumnMapping;
  includedKeys: Record<string, boolean>;
} | null {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      mapping?: ColumnMapping;
      includedKeys?: Record<string, boolean>;
    };
    if (!parsed.mapping || !parsed.includedKeys) return null;
    return { mapping: parsed.mapping, includedKeys: parsed.includedKeys };
  } catch {
    return null;
  }
}

export function saveBulkConfig(
  storageKey: string,
  mapping: ColumnMapping,
  includedKeys: Record<string, boolean>,
): void {
  window.localStorage.setItem(
    `${STORAGE_PREFIX}${storageKey}`,
    JSON.stringify({ mapping, includedKeys }),
  );
}
